<script setup lang="ts">
import { VisionAIFooterButton } from '@dashhudson/dashing-ui/button';
import { useDashToast } from '@dashhudson/dashing-ui/toast';
import { computed } from 'vue';

import { useVisionFloatingButtonLayout } from '@/composables/layout/useFloatingWidgetLayout';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';

const emit = defineEmits<{
  'toggle-chat-window': [];
}>();

const { isChatPanelOpen, chatPanelTransformStyle, buttonPositionClasses } =
  useVisionFloatingButtonLayout();

const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const { addToast } = useDashToast({ life: 5000 });

const showInsightPill = computed(
  () => !isChatPanelOpen.value && visionAiStore.isPendingCompletionPillExpanded,
);

const showLoadingPill = computed(
  () => !isChatPanelOpen.value && visionAiStore.isLoadingPillExpanded,
);

const isTaskInProgress = computed(
  () => visionAiConversationsStore.loadingConversationIds.length > 0,
);

const expanded = computed(() => showInsightPill.value || showLoadingPill.value);

// Insight Pill keeps the animation icon (loading=false). Loading Pill and the
// collapsed in-flight state both render the spinner (loading=true). When the
// panel is open, the button shows the chevron-right regardless of task state.
const loadingProp = computed(() => {
  if (isChatPanelOpen.value) return false;
  if (showInsightPill.value) return false;
  if (showLoadingPill.value) return true;
  return isTaskInProgress.value;
});

const statusMessage = computed(() => {
  if (showInsightPill.value) return 'Vision has finished a task';
  if (showLoadingPill.value) return 'Vision is working on a task...';
  return undefined;
});

const callToAction = computed(() => {
  if (showInsightPill.value) {
    return { label: 'View Response', icon: 'chevron-right' } as const;
  }
  if (showLoadingPill.value) {
    return { label: 'Open Vision', icon: 'chevron-right' } as const;
  }
  return undefined;
});

async function handleButtonClicked(event: MouseEvent) {
  event.stopPropagation();

  if (!isChatPanelOpen.value && visionAiStore.hasPendingCompletion) {
    const conversationId = visionAiStore.pendingCompletionConversationId;
    if (conversationId) {
      try {
        await visionAiConversationsStore.getConversationMessages({ conversationId });
        visionAiConversationsStore.setCurrentConversationId(conversationId);
        visionAiStore.consumePendingCompletion();
        visionAiStore.markConversationRead(conversationId);
      } catch {
        addToast({
          message: 'Could not load this conversation. Please try again.',
          severity: 'error',
        });
        // Fall through to open the panel so the user isn't trapped. The
        // watcher will clearPendingCompletion; the unread dot remains so the
        // sidebar conversation click is the retry path.
      }
    }
  }

  emit('toggle-chat-window');
}
</script>

<template>
  <div
    data-test="vision-ai-button-global"
    data-exclude-side-panel-close
    :class="['vision-ai-button !pointer-events-auto relative', buttonPositionClasses]"
    :style="[{ zIndex: '9999' }, chatPanelTransformStyle]"
  >
    <VisionAIFooterButton
      :open="isChatPanelOpen"
      :loading="loadingProp"
      :expanded="expanded"
      :status-message="statusMessage"
      :call-to-action="callToAction"
      :class="expanded ? 'hover:!scale-100' : undefined"
      enable-animation
      @click="handleButtonClicked"
    />
  </div>
</template>
